// useOlMapLifecycle.js — OpenLayers map mount/unmount + basemap-style-swap (ticket 05
// extraction). Owns baseTileLayerRef/basemapSwapQueueRef and the two effects that:
//   1) create the OL Map on mount and tear it down on unmount, and
//   2) race-safely swap the mapbox-style basemap whenever `basemap` changes.
//
// `mapRef` itself is created by the caller (MapEditorModalInner) rather than inside this hook:
// `useMapEditor()` needs the `mapRef` *ref object* passed in before this hook can know the
// resolved `basemap` value (which comes from `meta`, itself returned by `useMapEditor()`). So
// the ref has to be created before either hook runs, and both are handed the same instance.
//
// MapEditorModalInner calls this once and gets back the refs downstream hooks/JSX need.

import { useEffect, useRef } from 'react';
import OLMap from 'ol/Map';
import View from 'ol/View';
import { apply as applyMapboxStyle } from 'ol-mapbox-style';
import { fromLonLat } from 'ol/proj';
import { MIN_ZOOM } from '../../mapViewConstants';
import { createBasemapSwapQueue } from '../basemapSwapQueue';

export default function useOlMapLifecycle({ mapContainerRef, mapRef, basemap }) {
    const baseTileLayerRef = useRef(null); // ticket 02: GIBS satellite imagery TileLayer (lazily created — see applyImageryCoverage)
    // ponytail (ticket 03): one basemap-swap queue per modal instance so init-mount apply and
    // basemap-change swaps always serialize/stale-check against each other, never race.
    const basemapSwapQueueRef = useRef(null);
    if (!basemapSwapQueueRef.current) basemapSwapQueueRef.current = createBasemapSwapQueue();

    // -- Init OL Map on mount -------------------------------------------------
    useEffect(() => {
        const container = mapContainerRef.current;
        if (!container || mapRef.current) return;
        const map = new OLMap({
            target: container,
            layers: [],
            view: new View({ center: fromLonLat([0, 20]), zoom: 2, minZoom: MIN_ZOOM }),
        });
        mapRef.current = map;
        // ponytail: applyMapboxStyle fetches the OFM MapLibre style JSON and adds tile layers
        // automatically. Routed through basemapSwapQueueRef so this initial apply can't race
        // against — or outlive — a swap triggered right after mount / an unmount mid-fetch.
        basemapSwapQueueRef.current.request({
            map, basemapId: basemap,
            isMapCurrent: () => mapRef.current === map,
            applyStyle: applyMapboxStyle,
            onError: (e) => console.warn('[MapEditorModal] basemap apply failed', e),
        });
        return () => {
            map.setTarget(undefined);
            mapRef.current = null;
            baseTileLayerRef.current = null; // ticket 02: imagery TileLayer died with the map
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -- Swap basemap style when meta changes ---------------------------------
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        // ponytail (ticket 03): queued + stale-checked so rapid basemap switches always
        // converge on the last-requested style, and an in-flight fetch resolving after
        // unmount never touches a torn-down map.
        basemapSwapQueueRef.current.request({
            map, basemapId: basemap,
            isMapCurrent: () => mapRef.current === map,
            beforeApply: () => {
                map.getLayers().getArray().filter((l) => l.get('mapbox-layers')).forEach((l) => map.removeLayer(l));
            },
            applyStyle: applyMapboxStyle,
            onError: (e) => console.warn('[MapEditorModal] basemap swap failed', e),
        });
    }, [mapRef, basemap]);

    return { baseTileLayerRef };
}
